import React from "react";
import { Link } from "react-router-dom";
import { BRAND } from "../lib/siteData";
import { Instagram, Linkedin, Facebook, ArrowUp } from "lucide-react";

const LINKS_QUICK = [
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/projects", label: "Projects" },
  { to: "/investors", label: "Investors & Developers" },
  { to: "/contact", label: "Contact" },
];

const LINKS_SERVICES = [
  "Engineering",
  "Architectural Design",
  "Construction",
  "Project Management",
  "Land Development",
  "Quantity Surveying",
];

export default function Footer() {
  return (
    <footer className="relative bg-[#0A192F] text-white pt-20 pb-8 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 blueprint-grid-dark opacity-50 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 pb-14 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="bg-white p-2 flex items-center">
                <img
                  src={BRAND.logo}
                  alt="LNZ Designs & Development Co. Ltd"
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-sm text-white/65 leading-relaxed max-w-md">
              {BRAND.tagline} From concept to completion, LNZ is Jamaica&apos;s integrated engineering,
              architecture and construction partner — trusted by investors, developers and
              diaspora clients across the Caribbean.
            </p>

            <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
              {[
                { href: BRAND.socials.instagram, icon: Instagram, label: "Instagram" },
                { href: BRAND.socials.linkedin, icon: Linkedin, label: "LinkedIn" },
                { href: BRAND.socials.facebook, icon: Facebook, label: "Facebook" },
              ].map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 flex items-center justify-center border border-white/15 text-white/80 hover:bg-[#F5A400] hover:border-[#F5A400] hover:text-[#0A192F] transition-all"
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                >
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.3em] font-semibold text-[#F5A400] mb-5">NAVIGATE</div>
            <ul className="space-y-3">
              {LINKS_QUICK.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/80 hover:text-[#F5A400] transition-colors"
                    data-testid={`footer-quick-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.3em] font-semibold text-[#F5A400] mb-5">SERVICES</div>
            <ul className="space-y-3">
              {LINKS_SERVICES.map((l) => (
                <li key={l}>
                  <Link to="/services" className="text-sm text-white/80 hover:text-[#F5A400] transition-colors">
                    {l}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-2">
            <div className="text-[11px] tracking-[0.3em] font-semibold text-[#F5A400] mb-5">REACH US</div>
            <ul className="space-y-3 text-sm text-white/80">
              <li>{BRAND.phoneDisplay}</li>
              <li>{BRAND.email}</li>
              <li className="text-white/65">{BRAND.address}</li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-white/60" data-testid="footer-copyright">
            © {new Date().getFullYear()} {BRAND.name}. All rights reserved.
          </div>
          <div className="flex items-center gap-6 text-xs text-white/60">
            <Link to="/contact" className="hover:text-[#F5A400]">Privacy</Link>
            <Link to="/contact" className="hover:text-[#F5A400]">Terms</Link>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              className="inline-flex items-center gap-2 hover:text-[#F5A400]"
              data-testid="footer-back-to-top"
            >
              Back to top <ArrowUp className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
